import { useApolloClient } from '@apollo/client';
import { ContentCopy } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { SERVICE_AREA_TYPES } from 'admin/userRelations/userRelationUtils';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/ChevronRight.svg';
import { Avatar } from 'common/components/Avatar/Avatar';
import { ConfirmDialog, ConfirmDialogProps } from 'common/components/ConfirmDialog/ConfirmDialog';
import { useDialog } from 'common/hooks/useDialog';
import { useLoadingIndicator } from 'common/hooks/useLoadingIndicator';
import { useNotification } from 'common/hooks/useNotification';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthContext } from '../../common/providers/AuthContextProvider/AuthContextProvider';
import { Role, useAdminGetStaffQuery, useCurrentUserQuery, useRemoveUserMutation } from '../../generated/graphql';
import { ChangeAvatarDialog, ChangeAvatarDialogProps } from './dialogs/ChangeAvatar';
import { ChangeEmailDialog, ChangeEmailDialogProps } from './dialogs/ChangeEmailDialog';
import { ChangeNameDialog, ChangeNameDialogProps } from './dialogs/ChangeNameDialog';
import { ChangeDialogPasswordProps, ChangePasswordDialog } from './dialogs/ChangePasswordDialog';
import { ChangePhoneDialog, ChangePhoneDialogProps } from './dialogs/ChangePhoneDialog';
import { ChangeServiceAreasDialog, ChangeServiceAreasDialogProps } from './dialogs/ChangeServiceAreas';
import { ChangeServiceLocationsDialog, ChangeServiceLocationsDialogProps } from './dialogs/ChangeServiceLocations';
import { ChangeSurnameDialog, ChangeSurnameDialogProps } from './dialogs/ChangeSurnameDialog';
import { ChangeUsernameDialog, ChangeUsernameDialogProps } from './dialogs/ChangeUsernameDialog';
import { styles } from './SettingsPageContent.styles';
import { SettingsPageItem } from './SettingsPageItem';

interface SettingPageContentProps {
  showRemoveAccount?: boolean;
  showTitle?: boolean;
  showInvitationLink?: boolean;
  showAvatar?: boolean;
}

export const SettingsPageContent: React.FC<SettingPageContentProps> = ({
  showAvatar = false,
  showInvitationLink = false,
  showRemoveAccount = true,
  showTitle = true,
}) => {
  const { t } = useTranslation();

  const { end: endLoading, start: startLoading } = useLoadingIndicator(t('settings.removeUser.loading.message'));
  const notify = useNotification();

  const [removeUserMutation] = useRemoveUserMutation({
    onError: () => notify({ message: t('common:somethingWentWrong'), type: 'error' }),
  });

  const changeAvatarDialog = useDialog<ChangeAvatarDialogProps, void>(ChangeAvatarDialog, {
    instanceId: 'settings-change-profile-image',
  });

  const changeUsernameDialog = useDialog<ChangeUsernameDialogProps, void>(ChangeUsernameDialog, {
    instanceId: 'settings-change-username',
  });
  const changeNameDialog = useDialog<ChangeNameDialogProps, void>(ChangeNameDialog, {
    instanceId: 'settings-change-name',
  });
  const changeSurnameDialog = useDialog<ChangeSurnameDialogProps, void>(ChangeSurnameDialog, {
    instanceId: 'settings-change-surname',
  });
  const changePhoneDialog = useDialog<ChangePhoneDialogProps, void>(ChangePhoneDialog, {
    instanceId: 'settings-change-phone',
  });
  const changeEmailDialog = useDialog<ChangeEmailDialogProps, void>(ChangeEmailDialog, {
    instanceId: 'settings-change-email',
  });
  const changePasswordDialog = useDialog<ChangeDialogPasswordProps, void>(ChangePasswordDialog, {
    instanceId: 'settings-change-password',
  });
  const confirmDeleteDialog = useDialog<ConfirmDialogProps, boolean>(ConfirmDialog, {
    instanceId: 'confirm-applicant-delete',
  });
  const changeServiceLocationsDialog = useDialog<ChangeServiceLocationsDialogProps, void>(
    ChangeServiceLocationsDialog,
    {
      instanceId: 'settings-change-service-locations',
    },
  );
  const changeServiceAreasDialog = useDialog<ChangeServiceAreasDialogProps, void>(ChangeServiceAreasDialog, {
    instanceId: 'settings-change-service-areas',
  });

  const client = useApolloClient();

  const { isAuthenticated, signOut, userData: userAuthData } = useContext(AuthContext);
  const isFederatedLogin = !!userAuthData?.user.attributes.identities;
  const showPasswordChange = !isFederatedLogin;
  const { data: currentUserData } = useCurrentUserQuery({ skip: !isAuthenticated });
  const { data: staffData } = useAdminGetStaffQuery({
    skip: !isAuthenticated,
    variables: { id: currentUserData?.user?.staffId ?? '' },
  });
  const invitationLink = `${window.location.origin}/registration?utm_source=invitation&utm_medium=${currentUserData?.user?.subject}`;

  const hasAnyRole = (allowedRoles: Role[]) =>
    isAuthenticated && currentUserData?.user?.roles?.some((role) => allowedRoles.includes(role));

  const showChangeServiceLocations = hasAnyRole([Role.RoleFinancialAdvisor, Role.RoleScout]);
  const showChangeServiceAreas = hasAnyRole([Role.RoleFinancialAdvisor]);

  const handleChangeAvatar = async () => {
    changeAvatarDialog.create({});
  };

  const handleChangeEmail = async () => {
    if (!isFederatedLogin) {
      changeEmailDialog.create({});
    }
  };

  const handleChangePassword = async () => {
    changePasswordDialog.create({});
  };

  const handleRemoveAccount = async () => {
    const confirmed = await confirmDeleteDialog.create({
      confirmButtonText: t('settings.removeDialog.confirm'),
      title: t('settings.removeDialog.title'),
    });

    if (confirmed) {
      startLoading();
      const resp = await removeUserMutation();

      if (resp.data?.removeUser) {
        await signOut('/removed-user');
        client.clearStore();
      }
      endLoading();
    }
  };

  const handleChangeUsername = async () => {
    changeUsernameDialog.create({});
  };

  const handleChangeName = async () => {
    changeNameDialog.create({});
  };

  const handleChangeSurname = async () => {
    changeSurnameDialog.create({});
  };

  const handleChangePhone = async () => {
    changePhoneDialog.create({});
  };

  const handleCopyInvitationLink = async () => {
    navigator.clipboard.writeText(invitationLink);
    notify({ message: t('settings.invitationLink.copiedToClipboard'), type: 'success' });
  };

  const handleChangeServiceLocations = async () => {
    changeServiceLocationsDialog.create({});
  };

  const handleChangeServiceAreas = async () => {
    changeServiceAreasDialog.create({});
  };

  return (
    <Box>
      {showTitle && (
        <Box sx={styles.headline}>
          <Typography component="h2" sx={styles.headlineText} variant="headlineL">
            {t('settings.title')}
          </Typography>
        </Box>
      )}
      {showAvatar && (
        // <Box>
        <Avatar avatar={currentUserData?.user?.avatar} onClick={handleChangeAvatar} size={200} sx={styles.avatar} />
        // </Box>
      )}
      <Stack sx={styles.content}>
        <SettingsPageItem
          item={t('settings.username')}
          onClick={handleChangeUsername}
          value={userAuthData?.user?.attributes.preferred_username ?? ''}
        />
        <SettingsPageItem
          item={t('settings.name')}
          onClick={handleChangeName}
          value={userAuthData?.user?.attributes.given_name ?? ''}
        />
        <SettingsPageItem
          item={t('settings.surname')}
          onClick={handleChangeSurname}
          value={userAuthData?.user?.attributes.family_name ?? ''}
        />
        <SettingsPageItem
          disabled={isFederatedLogin}
          item={t('settings.email')}
          onClick={handleChangeEmail}
          tooltip={isFederatedLogin ? t('settings.changeDialog.email.tooltip') : undefined}
          value={userAuthData?.user?.attributes.email ?? ''}
        />
        <SettingsPageItem
          item={t('settings.phone')}
          onClick={handleChangePhone}
          value={userAuthData?.user?.attributes.phone_number ?? ''}
        />
        {showPasswordChange && (
          <SettingsPageItem item={t('settings.password')} onClick={handleChangePassword} value="**********" />
        )}
        {showRemoveAccount && (
          <SettingsPageItem
            icon={<ChevronRightIcon />}
            item={t('settings.removeAccount')}
            onClick={handleRemoveAccount}
            value=""
          />
        )}
        {showInvitationLink && (
          <SettingsPageItem
            icon={<ContentCopy />}
            item={t('settings.invitationLink.label')}
            onClick={handleCopyInvitationLink}
            tooltip={t('settings.invitationLink.tooltip')}
            value={invitationLink}
          />
        )}
        {showChangeServiceLocations && (
          <SettingsPageItem
            item={t('settings.serviceLocations')}
            onClick={handleChangeServiceLocations}
            value={
              staffData?.adminGetStaff?.serviceLocations
                ?.map((serviceLocation) => serviceLocation.name)
                .sort((a, b) => a.localeCompare(b))
                .join(', ') ?? ''
            }
          />
        )}
        {showChangeServiceAreas && (
          <SettingsPageItem
            item={t('settings.serviceAreas')}
            onClick={handleChangeServiceAreas}
            value={
              staffData?.adminGetStaff?.serviceAreas
                ?.map(
                  (serviceArea) =>
                    SERVICE_AREA_TYPES.find((serviceAreaType) => serviceAreaType.id === serviceArea)?.name ??
                    serviceArea,
                )
                .sort((a, b) => a.localeCompare(b))
                .join(', ') ?? ''
            }
          />
        )}
      </Stack>
    </Box>
  );
};
